import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DraftCartLine, Product } from "@/lib/types";
import { RightDrawer } from "./RightDrawer";
import { QtyStepper } from "./QtyStepper";
import { ParticipantBadge } from "./ParticipantBadge";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { BinIcon } from "./icons/BinIcon";
import { withImageVersion } from "@/lib/imageUrl";
import { getTranslatedText } from "@/lib/translations";
import { compositionRows } from "@/lib/composition";

interface Props {
  open: boolean;
  onClose: () => void;
  lines: Record<string, DraftCartLine>;
  productsById: Map<string, Product>;
  /** Map of participantId → nickname (when known). */
  nicknamesById: Record<string, string>;
  /** Per-line max qty cap (mirrors tablet ProductCartRow.computeMaxQuantity). */
  lineMaxQty: (line: DraftCartLine) => number;
  isFrozen: boolean;
  myParticipantId: string;
  onUpdateQty: (lineId: string, qty: number) => void;
  onRemoveLine: (lineId: string) => void;
  onSubmit: () => void;
  submitDisabled: boolean;
  submitLabel: string;
  submitHint?: string;
  /** « Dernière commande à HH:MM » — averti avant la fermeture, indépendant du blocage. */
  deadlineNotice?: string;
}

interface PendingDelete {
  lineId: string;
  productName: string;
  isMe: boolean;
  /** Owner's display name — used in the non-owner-removal confirm copy. */
  ownerName?: string;
}

interface GroupedLines {
  participantId: string;
  isMe: boolean;
  total: number;
  lines: Array<[string, DraftCartLine]>;
}

const computeLineSubtotal = (line: DraftCartLine): number => {
  const extras = (line.options ?? []).reduce(
    (s, r) => s + r.data.reduce((ss, it) => ss + (it.unitPrice || 0) * (it.quantity || 1), 0),
    0
  );
  return (line.unitPrice + extras) * line.qty;
};

export const SharedCartView = ({
  open,
  onClose,
  lines,
  productsById,
  nicknamesById,
  lineMaxQty,
  isFrozen,
  myParticipantId,
  onUpdateQty,
  onRemoveLine,
  onSubmit,
  submitDisabled,
  submitLabel,
  submitHint,
  deadlineNotice,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const entries = useMemo(() => Object.entries(lines), [lines]);
  const lineName = (line: DraftCartLine): string => {
    const product = productsById.get(line.productId);
    return (
      (product ? getTranslatedText(product, "name", i18n.language) : undefined) ??
      line.productSnapshot?.name ??
      line.productId
    );
  };
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const askDelete = (lineId: string, line: DraftCartLine, isMe: boolean): void => {
    const productName = lineName(line);
    const ownerName =
      (line.addedBy && nicknamesById[line.addedBy]) ||
      t("cartDrawer.participantFallback", "Le participant");
    setPendingDelete({ lineId, productName, isMe, ownerName });
  };

  const handleQtyChange = (
    lineId: string,
    line: DraftCartLine,
    newQty: number
  ): void => {
    void line;
    if (newQty <= 0) onRemoveLine(lineId);
    else onUpdateQty(lineId, newQty);
  };

  const confirmDelete = (): void => {
    if (!pendingDelete) return;
    onRemoveLine(pendingDelete.lineId);
    setPendingDelete(null);
  };

  const total = useMemo(
    () => entries.reduce((s, [, l]) => s + computeLineSubtotal(l), 0),
    [entries]
  );

  const totalQty = useMemo(
    () => entries.reduce((s, [, l]) => s + (l.qty || 0), 0),
    [entries]
  );

  const groups: GroupedLines[] = useMemo(() => {
    const map = new Map<string, GroupedLines>();
    for (const [lineId, line] of entries) {
      const pid = line.addedBy || "unknown";
      const existing = map.get(pid);
      const subtotal = computeLineSubtotal(line);
      if (existing) {
        existing.total += subtotal;
        existing.lines.push([lineId, line]);
      } else {
        map.set(pid, {
          participantId: pid,
          isMe: pid === myParticipantId,
          total: subtotal,
          lines: [[lineId, line]],
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
      return a.participantId.localeCompare(b.participantId);
    });
  }, [entries, myParticipantId]);

  const isEmpty = !entries.length;

  const totalLabel = total.toFixed(2);
  const summary =
    totalQty === 0
      ? t("cartDrawer.empty", "Aucun article")
      : totalQty > 1
      ? t("cartDrawer.summaryMany", {
          count: totalQty,
          total: totalLabel,
          defaultValue: `${totalQty} articles · ${totalLabel} €`,
        })
      : t("cartDrawer.summaryOne", {
          count: totalQty,
          total: totalLabel,
          defaultValue: `${totalQty} article · ${totalLabel} €`,
        });

  const header = (
    <header className="px-5 pt-5 pb-3 border-b border-border flex items-center justify-between gap-3 pt-safe">
      <div className="min-w-0 flex-1">
        <h2 className="display-font text-2xl font-semibold text-ink leading-tight">
          {t("cartDrawer.heading", "Panier partagé")}
        </h2>
        <p className="text-sm text-ink-soft mt-0.5">{summary}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="w-10 h-10 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink shrink-0"
        aria-label={t("cartDrawer.closeAria", "Fermer le panier")}
      >
        ✕
      </button>
    </header>
  );

  const footer = (
    <div>
      <button
        type="button"
        disabled={submitDisabled}
        onClick={onSubmit}
        className="w-full bg-primary text-white rounded-button py-4 px-5 font-semibold shadow-warm flex items-center justify-between disabled:opacity-30 transition-colors hover:bg-primary-hover"
      >
        <span>{submitLabel}</span>
        {!isEmpty && <span className="tabular-nums">{total.toFixed(2)} €</span>}
      </button>
      {deadlineNotice && (
        <p className="text-xs text-warm-deep text-center mt-2 font-medium">{deadlineNotice}</p>
      )}
      {submitHint && (
        <p className="text-xs text-ink-muted text-center mt-2">{submitHint}</p>
      )}
    </div>
  );

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      ariaLabel={t("cartDrawer.heading", "Panier partagé")}
      header={header}
      footer={footer}
    >
      <div className="px-5 pt-4 pb-6">
        {isEmpty && (
          <div className="text-center py-12">
            <div className="text-4xl mb-3" aria-hidden="true">
              🍽️
            </div>
            <p className="text-ink-soft text-sm max-w-xs mx-auto">
              {t(
                "cartDrawer.emptyDescription",
                "Le panier est vide. Ajoute des plats depuis le menu — ils s'afficheront ici en temps réel pour toute la table."
              )}
            </p>
          </div>
        )}

        {!isEmpty && (
          <ul className="space-y-5">
            {groups.map((group) => (
              <li key={group.participantId}>
                <header className="flex items-center gap-2 mb-2">
                  <ParticipantBadge
                    participantId={group.participantId}
                    displayName={nicknamesById[group.participantId]}
                    isMe={group.isMe}
                    size="sm"
                  />
                  <span className="text-sm font-medium text-ink">
                    {group.isMe
                      ? t("cartDrawer.youWithName", {
                          name:
                            nicknamesById[group.participantId] ??
                            t("cartDrawer.you", "Toi"),
                          defaultValue: `${
                            nicknamesById[group.participantId] ?? "Toi"
                          } (moi)`,
                        })
                      : nicknamesById[group.participantId] ??
                        t("cartDrawer.participant", {
                          id: group.participantId.slice(0, 4),
                          defaultValue: `Participant ${group.participantId.slice(0, 4)}`,
                        })}
                  </span>
                  <span className="flex-1 h-px bg-border" aria-hidden />
                  <span className="text-xs text-ink-muted tabular-nums">
                    {group.total.toFixed(2)} €
                  </span>
                </header>
                <ul className="space-y-2">
                  {group.lines.map(([lineId, line]) => {
                    const product = productsById.get(line.productId);
                    const subtotal = computeLineSubtotal(line);
                    const extrasCount = (line.options ?? []).reduce(
                      (s, r) => s + r.data.length,
                      0
                    );
                    const composition = compositionRows(line.options);
                    const imagePath =
                      product?.image?.path ?? line.productSnapshot?.image?.path;
                    return (
                      <li
                        key={lineId}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-button bg-surface shadow-warm-sm"
                      >
                        <div className="w-14 h-14 rounded-button overflow-hidden flex-shrink-0 bg-pink-softer">
                          {imagePath ? (
                            <img
                              src={withImageVersion(imagePath)}
                              alt=""
                              width={56}
                              height={56}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-medium text-ink truncate">
                              {lineName(line)}
                            </span>
                            <span className="tabular-nums text-sm text-ink-soft shrink-0">
                              {subtotal.toFixed(2)} €
                            </span>
                          </div>
                          {composition.rows.length > 0 ? (
                            <ul className="mt-1 space-y-0.5 text-xs text-ink-muted">
                              {composition.rows.map((row) => (
                                <li key={row.key}>
                                  <span className="text-ink-soft">
                                    {row.quantity > 1 && `${row.quantity}× `}
                                    {row.name}
                                  </span>
                                  {row.options.length > 0 && ` · ${row.options.join(" · ")}`}
                                </li>
                              ))}
                              {composition.extras.length > 0 && (
                                <li>{composition.extras.join(" · ")}</li>
                              )}
                            </ul>
                          ) : extrasCount > 0 && (
                            <p className="text-xs text-ink-muted mt-0.5 line-clamp-1">
                              {(line.options ?? [])
                                .flatMap((r) => r.data.map((it) => it.name))
                                .join(" · ")}
                            </p>
                          )}
                          <div className="mt-2 flex items-center gap-3">
                            {group.isMe ? (
                              <QtyStepper
                                value={line.qty}
                                onChange={(n) => handleQtyChange(lineId, line, n)}
                                min={0}
                                max={lineMaxQty(line)}
                                size="sm"
                                disabled={isFrozen}
                                showRemoveAtOne
                              />
                            ) : !isFrozen ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-ink-soft tabular-nums">
                                  ×{line.qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => askDelete(lineId, line, false)}
                                  className="w-8 h-8 rounded-full bg-bg border border-border flex items-center justify-center text-ink hover:border-danger hover:text-danger transition-colors"
                                  aria-label={t("cartDrawer.removeAria", "Retirer cet article")}
                                >
                                  <BinIcon size={16} />
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-ink-soft tabular-nums">
                                ×{line.qty}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        title={t("modalDelete.title", "Retirer cet article ?")}
        description={
          pendingDelete?.isMe
            ? t("modalDelete.descriptionMine", {
                name: pendingDelete.productName,
                defaultValue: `« ${pendingDelete.productName} » sera supprimé de votre panier.`,
              })
            : pendingDelete
            ? t("modalDelete.descriptionOther", {
                name: pendingDelete.productName,
                nickname: pendingDelete.ownerName ?? "",
                defaultValue: `« ${pendingDelete.productName} » sera retiré du panier de la table. ${pendingDelete.ownerName ?? "Le participant"} en sera averti.`,
              })
            : undefined
        }
        confirmLabel={t("modalDelete.confirm", "Retirer")}
        cancelLabel={t("modalDelete.cancel", "Annuler")}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </RightDrawer>
  );
};
